import AsyncStorage from '@/utils/async-storage-chunker';
import {
  AdditionalInputValueInput,
  CreditRequest_OfflineDataQuery,
  ItemsContext_MultiOrderGuideItemsQuery,
  NewOrder_CreateOrderMutationVariables,
  NewOrder_OrderContextQuery,
  NewOrder_OrderFieldsFragment,
  NewOrder_VisibleVariantPackQuery,
  PriceEditing_GetPriceEditDisplayInfoQuery,
  PrintTagBatchFullFieldsFragment,
  PrintTagTemplateDetailFieldsFragment,
  UserContext_EmployeeQuery,
  VariantPackInfoContext_OrderConstraintsQuery,
  VariantPackInfoContext_OrderGuideInfoListQuery,
  VariantPackPricesResponse,
} from '@/types';
import {
  getOfflineKey,
  OFFLINE_DATA_KEY_PREFIX,
  OfflineAuthToken,
  OfflineData,
  OfflineSyncType,
} from '@/utils/offline-mode';
import { OperationVariables } from '@apollo/client';
import { SalesRepOverrideMode } from '@/enums';
import { v4 as uuidv4 } from 'uuid';

export interface OfflineAdapter<T> {
  fetch(): Promise<T | null>;
  remove(): Promise<void>;
  save(user: T): Promise<void>;
}

export interface OfflineOrderContext {
  newOrderContext: NewOrder_OrderContextQuery;
  synced: boolean;
}

export enum OfflineOrderContextAdapterEvent {
  Save = 'save:orderContext',
}

export class OfflineOrderContextAdapter
  implements OfflineAdapter<OfflineOrderContext>
{
  private static instanceMap: {
    [instanceKey: string]: OfflineOrderContextAdapter;
  } = {};
  private static listeners: {
    [listenerId: string]: (event: OfflineOrderContextAdapterEvent) => void;
  } = {};
  restaurantUUID: string;
  supplierUUID: string;

  private constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.OrderContext, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  public static getInstance(
    restaurantUUID: string,
    supplierUUID: string,
  ): OfflineOrderContextAdapter {
    const instanceKey = OfflineOrderContextAdapter.generateInstanceKey(
      restaurantUUID,
      supplierUUID,
    );

    if (!OfflineOrderContextAdapter.instanceMap[instanceKey]) {
      const adapter = new OfflineOrderContextAdapter(
        restaurantUUID,
        supplierUUID,
      );
      OfflineOrderContextAdapter.instanceMap[instanceKey] = adapter;
    }

    return OfflineOrderContextAdapter.instanceMap[instanceKey];
  }

  static async getAllCacheKeys(): Promise<string[]> {
    return (await AsyncStorage.getAllKeys()).filter(
      (key) =>
        key.startsWith(
          `${OFFLINE_DATA_KEY_PREFIX}:${OfflineData.OrderContext}_`,
        ) && !AsyncStorage.isChunkKey(key),
    );
  }

  private static generateInstanceKey(
    restaurantUUID: string,
    supplierUUID: string,
  ): string {
    return `${restaurantUUID}:${supplierUUID}`;
  }

  async fetch(): Promise<OfflineOrderContext | null> {
    const orderContextData = await AsyncStorage.getItem(this.cacheKey);
    if (!orderContextData) {
      return null;
    }
    const transformedOrderContextData = JSON.parse(orderContextData);

    // To support backwards compatibility with the old order context, we need to transform the orders array to the getOrders array
    // https://github.com/usepepper/shishito/pull/8148

    if (
      transformedOrderContextData &&
      transformedOrderContextData.newOrderContext &&
      'orders' in transformedOrderContextData.newOrderContext
    ) {
      transformedOrderContextData.newOrderContext.getOrders =
        transformedOrderContextData.newOrderContext.orders.map(
          (order: NewOrder_OrderFieldsFragment) => ({
            order,
          }),
        );
      delete transformedOrderContextData.newOrderContext.orders;
    }

    return transformedOrderContextData;
  }

  async save(orderContext: OfflineOrderContext): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(orderContext));
    for (let listener of Object.values(OfflineOrderContextAdapter.listeners)) {
      listener(OfflineOrderContextAdapterEvent.Save);
    }
  }

  async removeOrder(orderUUID: string): Promise<void> {
    const offlineOrderContext = await this.fetch();
    if (
      !offlineOrderContext ||
      !offlineOrderContext.newOrderContext ||
      !offlineOrderContext.newOrderContext.getOrders
    ) {
      return;
    }

    await this.save({
      newOrderContext: {
        ...offlineOrderContext.newOrderContext,
        getOrders:
          offlineOrderContext.newOrderContext.getOrders.filter(
            (o) => o.order?.uuid !== orderUUID,
          ) || [],
      },
      synced: true,
    });
  }

  addEventListener(callback: (event: string) => void) {
    const listenerId = uuidv4();

    OfflineOrderContextAdapter.listeners[listenerId] = callback;
    return listenerId;
  }

  removeEventListener(listenerId: string) {
    delete OfflineOrderContextAdapter.listeners[listenerId];
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }
}

export class OfflineOrderGuideAdapter
  implements OfflineAdapter<ItemsContext_MultiOrderGuideItemsQuery>
{
  restaurantUUID: string;
  supplierUUID: string;

  constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.OrderGuide, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<ItemsContext_MultiOrderGuideItemsQuery | null> {
    const orderGuideData = await AsyncStorage.getItem(this.cacheKey);
    return orderGuideData ? JSON.parse(orderGuideData) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(
    orderGuide: ItemsContext_MultiOrderGuideItemsQuery,
  ): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(orderGuide));
  }
}

export class OfflineOrderConstraintsAdapter
  implements OfflineAdapter<VariantPackInfoContext_OrderConstraintsQuery>
{
  restaurantUUID: string;
  supplierUUID: string;

  constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.OrderConstraints, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<VariantPackInfoContext_OrderConstraintsQuery | null> {
    const orderConstraintsData = await AsyncStorage.getItem(this.cacheKey);
    return orderConstraintsData ? JSON.parse(orderConstraintsData) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(
    orderConstraints: VariantPackInfoContext_OrderConstraintsQuery,
  ): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(orderConstraints));
  }
}

export class OfflineOrderGuidePriceEditDisplayInfoAdapter
  implements OfflineAdapter<PriceEditing_GetPriceEditDisplayInfoQuery>
{
  restaurantUUID: string;
  supplierUUID: string;

  constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.OrderGuidePriceEditDisplayInfo, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<PriceEditing_GetPriceEditDisplayInfoQuery | null> {
    const priceEditDisplayInfoData = await AsyncStorage.getItem(this.cacheKey);
    return priceEditDisplayInfoData
      ? JSON.parse(priceEditDisplayInfoData)
      : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(
    priceEditDisplayInfo: PriceEditing_GetPriceEditDisplayInfoQuery,
  ): Promise<void> {
    await AsyncStorage.setItem(
      this.cacheKey,
      JSON.stringify(priceEditDisplayInfo),
    );
  }
}

type VariantPackInfoAdapterInstanceKey = string; // restaurantUUID:supplierUUID
export enum VariantPackInfoAdapterEvent {
  Save = 'save:variantPackInfo',
}

export class OfflineVariantPackInfoAdapter
  implements OfflineAdapter<VariantPackInfoContext_OrderGuideInfoListQuery>
{
  private static instanceMap: {
    [
      instanceKey: VariantPackInfoAdapterInstanceKey
    ]: OfflineVariantPackInfoAdapter;
  } = {};
  private static listeners: {
    [listenerId: string]: (event: VariantPackInfoAdapterEvent) => void;
  } = {};
  restaurantUUID: string;
  supplierUUID: string;

  private constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.VariantPackInfo, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  public static getInstance(
    restaurantUUID: string,
    supplierUUID: string,
  ): OfflineVariantPackInfoAdapter {
    const instanceKey = OfflineVariantPackInfoAdapter.generateInstanceKey(
      restaurantUUID,
      supplierUUID,
    );

    if (!OfflineVariantPackInfoAdapter.instanceMap[instanceKey]) {
      const adapter = new OfflineVariantPackInfoAdapter(
        restaurantUUID,
        supplierUUID,
      );
      OfflineVariantPackInfoAdapter.instanceMap[instanceKey] = adapter;
    }

    return OfflineVariantPackInfoAdapter.instanceMap[instanceKey];
  }

  private static generateInstanceKey(
    restaurantUUID: string,
    supplierUUID: string,
  ): VariantPackInfoAdapterInstanceKey {
    return `${restaurantUUID}:${supplierUUID}`;
  }

  async fetch(): Promise<VariantPackInfoContext_OrderGuideInfoListQuery | null> {
    const variantPackInfoData = await AsyncStorage.getItem(this.cacheKey);
    return variantPackInfoData ? JSON.parse(variantPackInfoData) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(
    variantPackInfo: VariantPackInfoContext_OrderGuideInfoListQuery,
  ): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(variantPackInfo));

    for (let listener of Object.values(
      OfflineVariantPackInfoAdapter.listeners,
    )) {
      listener(VariantPackInfoAdapterEvent.Save);
    }
  }

  addEventListener(callback: (event: string) => void) {
    const listenerId = uuidv4();

    OfflineVariantPackInfoAdapter.listeners[listenerId] = callback;
    return listenerId;
  }

  removeEventListener(listenerId: string) {
    delete OfflineVariantPackInfoAdapter.listeners[listenerId];
  }
}

export class OfflineVisibleVariantPackAdapter
  implements OfflineAdapter<NewOrder_VisibleVariantPackQuery>
{
  restaurantUUID: string;
  supplierUUID: string;

  constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.VisibleVariantPacks, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<NewOrder_VisibleVariantPackQuery | null> {
    const visibleVariantPacksData = await AsyncStorage.getItem(this.cacheKey);
    return visibleVariantPacksData ? JSON.parse(visibleVariantPacksData) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(
    visibleVariantPacks: NewOrder_VisibleVariantPackQuery,
  ): Promise<void> {
    await AsyncStorage.setItem(
      this.cacheKey,
      JSON.stringify(visibleVariantPacks),
    );
  }
}

export class OfflineUserContextAdapter
  implements OfflineAdapter<UserContext_EmployeeQuery>
{
  userUUID: string;

  constructor(userUUID: string) {
    this.userUUID = userUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.UserContext, {
      userUUID: this.userUUID,
    });
  }

  async fetch(): Promise<UserContext_EmployeeQuery | null> {
    const userData = await AsyncStorage.getItem(this.cacheKey);
    return userData ? JSON.parse(userData) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(user: UserContext_EmployeeQuery): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(user));
  }
}

export class OfflineAuthAdapter implements OfflineAdapter<OfflineAuthToken> {
  get cacheKey(): string {
    return getOfflineKey(OfflineData.AuthToken);
  }

  async fetch(): Promise<OfflineAuthToken | null> {
    const authToken = await AsyncStorage.getItem(this.cacheKey);
    return authToken ? JSON.parse(authToken) : null;
  }

  async remove() {
    return await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(authToken: OfflineAuthToken) {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(authToken));
  }
}

export type OfflineDataStatus =
  | Partial<Record<OfflineSyncType, { downloadedDate: string }>>
  // Single downloadedDate is deprecated in favor of the mapped version
  | { downloadedDate: string };

export interface OfflineDataStatusMap {
  [restaurantUUID: string]: OfflineDataStatus;
}

export class OfflineDataStatusAdapter
  implements OfflineAdapter<OfflineDataStatusMap>
{
  get cacheKey(): string {
    return getOfflineKey(OfflineData.DataStatus);
  }

  async fetch(): Promise<OfflineDataStatusMap | null> {
    const dataStatus = await AsyncStorage.getItem(this.cacheKey);
    return dataStatus ? JSON.parse(dataStatus) : null;
  }

  async remove() {
    return await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(dataStatus: OfflineDataStatusMap): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(dataStatus));
  }
}

export interface OfflineOrderData
  extends NewOrder_CreateOrderMutationVariables {
  additionalInputValues?: AdditionalInputValueInput[]; // Optional for backwards compatibility
  orderNotes: string | null | undefined;
  ordersItems: NewOrder_OrderFieldsFragment['orders_items'];
  orderTotalPrice: string | null;
  poNumber: string | null | undefined;
  variantPacksPriceEditMapAtTimeOfOrder: VariantPacksPriceEditMap | null;
}

export interface OfflineQueuedOrdersMap {
  [orderUUID: string]: OfflineOrderData;
}

export class OfflineQueuedOrdersAdapter
  implements OfflineAdapter<OfflineQueuedOrdersMap>
{
  restaurantUUID: string;
  supplierUUID: string;

  constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.QueuedOrders, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<OfflineQueuedOrdersMap | null> {
    const queuedOrdersMap = await AsyncStorage.getItem(this.cacheKey);
    return queuedOrdersMap ? JSON.parse(queuedOrdersMap) : null;
  }

  async remove() {
    return await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(queuedOrders: OfflineQueuedOrdersMap): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(queuedOrders));
  }
}

type supplierUUID = string;

export interface RestaurantsWithQueuedOrdersMap {
  /** Maps a restaurant's UUID to its corresponding supplier's UUID. */
  [restaurantUUID: string]: supplierUUID;
}

export enum OfflineRestaurantsWithQueuedOrdersAdapterEvent {
  Remove = 'remove:order',
  Save = 'save:order',
}

export class OfflineRestaurantsWithQueuedOrdersAdapter
  implements OfflineAdapter<RestaurantsWithQueuedOrdersMap>
{
  private static instance: OfflineRestaurantsWithQueuedOrdersAdapter;
  private static listeners: [
    (event: OfflineRestaurantsWithQueuedOrdersAdapterEvent) => void,
  ];

  private constructor() {}

  get cacheKey(): string {
    return getOfflineKey(OfflineData.RestaurantsWithQueuedOrders);
  }

  public static getInstance(): OfflineRestaurantsWithQueuedOrdersAdapter {
    if (!OfflineRestaurantsWithQueuedOrdersAdapter.instance) {
      const simulator = new OfflineRestaurantsWithQueuedOrdersAdapter();
      OfflineRestaurantsWithQueuedOrdersAdapter.instance = simulator;
    }
    return OfflineRestaurantsWithQueuedOrdersAdapter.instance;
  }

  async fetch(): Promise<RestaurantsWithQueuedOrdersMap | null> {
    const restaurantsWithQueuedOrdersMap = await AsyncStorage.getItem(
      this.cacheKey,
    );
    return restaurantsWithQueuedOrdersMap
      ? JSON.parse(restaurantsWithQueuedOrdersMap)
      : null;
  }

  async remove() {
    await AsyncStorage.removeItem(this.cacheKey);
    for (let listener of OfflineRestaurantsWithQueuedOrdersAdapter.listeners) {
      listener(OfflineRestaurantsWithQueuedOrdersAdapterEvent.Remove);
    }
  }

  async save(
    restaurantsWithQueuedOrdersMap: RestaurantsWithQueuedOrdersMap,
  ): Promise<void> {
    await AsyncStorage.setItem(
      this.cacheKey,
      JSON.stringify(restaurantsWithQueuedOrdersMap),
    );
    for (let listener of OfflineRestaurantsWithQueuedOrdersAdapter.listeners) {
      listener(OfflineRestaurantsWithQueuedOrdersAdapterEvent.Save);
    }
  }

  addEventListener(callback: (event: string) => void) {
    if (!OfflineRestaurantsWithQueuedOrdersAdapter.listeners) {
      OfflineRestaurantsWithQueuedOrdersAdapter.listeners = [callback];
    } else {
      OfflineRestaurantsWithQueuedOrdersAdapter.listeners.push(callback);
    }
  }
}

export interface BatchesMap {
  [batchUUID: string]: PrintTagBatchFullFieldsFragment;
}

export interface BatchPricesMap {
  [variantPackUUID: string]: VariantPackPricesResponse;
}

export interface BatchTemplatesMap {
  [templateUUID: string]: PrintTagTemplateDetailFieldsFragment;
}

export interface BatchTagPrintingData {
  batches: BatchesMap | null;
  prices: BatchPricesMap | null;
  templates: BatchTemplatesMap | null;
}

export enum BatchTagPrintingAdapterEvent {
  Save = 'save:batchTagPrinting',
}

export class OfflineBatchTagPrintingAdapter
  implements OfflineAdapter<BatchTagPrintingData>
{
  private static instanceMap: {
    [instanceKey: string]: OfflineBatchTagPrintingAdapter;
  } = {};

  private static listeners: {
    [listenerId: string]: (event: BatchTagPrintingAdapterEvent) => void;
  } = {};

  restaurantUUID: string;
  supplierUUID: string;

  public static getInstance(
    restaurantUUID: string,
    supplierUUID: string,
  ): OfflineBatchTagPrintingAdapter {
    const instanceKey = OfflineBatchTagPrintingAdapter.generateInstanceKey(
      restaurantUUID,
      supplierUUID,
    );

    if (!OfflineBatchTagPrintingAdapter.instanceMap[instanceKey]) {
      const adapter = new OfflineBatchTagPrintingAdapter(
        restaurantUUID,
        supplierUUID,
      );
      OfflineBatchTagPrintingAdapter.instanceMap[instanceKey] = adapter;
    }

    return OfflineBatchTagPrintingAdapter.instanceMap[instanceKey];
  }

  private constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  private static generateInstanceKey(
    restaurantUUID: string,
    supplierUUID: string,
  ): string {
    return `${restaurantUUID}:${supplierUUID}`;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.BatchTagPrinting, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<BatchTagPrintingData | null> {
    const batchTagPrinting = await AsyncStorage.getItem(this.cacheKey);
    return batchTagPrinting ? JSON.parse(batchTagPrinting) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(batchTagPrinting: BatchTagPrintingData): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(batchTagPrinting));

    for (let listener of Object.values(
      OfflineBatchTagPrintingAdapter.listeners,
    )) {
      listener(BatchTagPrintingAdapterEvent.Save);
    }
  }

  addEventListener(callback: (event: string) => void) {
    const listenerId = uuidv4();

    OfflineBatchTagPrintingAdapter.listeners[listenerId] = callback;
    return listenerId;
  }

  removeEventListener(listenerId: string) {
    delete OfflineBatchTagPrintingAdapter.listeners[listenerId];
  }
}

export interface PriceEditInfo {
  type: SalesRepOverrideMode.GROSS_PROFIT | SalesRepOverrideMode.PRICE;
  value: number | null;
}

export interface VariantPacksPriceEditMap {
  [variantPackId: string]: PriceEditInfo;
}

export interface ChatVariantPacksPriceEditMap {
  // Key is <supplier_uuid>_<restaurant_uuid>
  [supplierRestaurantId: string]: VariantPacksPriceEditMap;
}

export class OfflineVariantPackPriceEditAdapter
  implements OfflineAdapter<ChatVariantPacksPriceEditMap>
{
  get cacheKey(): string {
    return getOfflineKey(OfflineData.PriceEdits);
  }

  async fetch(): Promise<ChatVariantPacksPriceEditMap | null> {
    const priceEditData = await AsyncStorage.getItem(this.cacheKey);
    return priceEditData ? JSON.parse(priceEditData) : null;
  }

  async save(
    priceEditVariantPackMap: ChatVariantPacksPriceEditMap,
  ): Promise<void> {
    await AsyncStorage.setItem(
      this.cacheKey,
      JSON.stringify(priceEditVariantPackMap),
    );
  }

  async remove() {
    return await AsyncStorage.removeItem(this.cacheKey);
  }
}

export class OfflineCreditRequestDataAdapter
  implements OfflineAdapter<CreditRequest_OfflineDataQuery>
{
  restaurantUUID: string;
  supplierUUID: string;

  constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.CreditRequestData, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  async fetch(): Promise<CreditRequest_OfflineDataQuery | null> {
    const creditRequestData = await AsyncStorage.getItem(this.cacheKey);
    return creditRequestData ? JSON.parse(creditRequestData) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(creditRequestData: CreditRequest_OfflineDataQuery): Promise<void> {
    await AsyncStorage.setItem(
      this.cacheKey,
      JSON.stringify(creditRequestData),
    );
  }
}

// Used for local scan UX (check if VP uuid is visible to a chat)
export class OfflineVisibleVariantPackIDAdapter
  implements OfflineAdapter<string[]>
{
  private static instanceMap: {
    [key: string]: OfflineVisibleVariantPackIDAdapter;
  } = {};

  restaurantUUID: string;
  supplierUUID: string;

  private constructor(restaurantUUID: string, supplierUUID: string) {
    this.restaurantUUID = restaurantUUID;
    this.supplierUUID = supplierUUID;
  }

  public static getInstance(
    restaurantUUID: string,
    supplierUUID: string,
  ): OfflineVisibleVariantPackIDAdapter {
    const key = `${restaurantUUID}:${supplierUUID}`;

    if (!OfflineVisibleVariantPackIDAdapter.instanceMap[key]) {
      OfflineVisibleVariantPackIDAdapter.instanceMap[key] =
        new OfflineVisibleVariantPackIDAdapter(restaurantUUID, supplierUUID);
    }

    return OfflineVisibleVariantPackIDAdapter.instanceMap[key];
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.VisibleVariantPackIDs, {
      restaurantUUID: this.restaurantUUID,
      supplierUUID: this.supplierUUID,
    });
  }

  private get downloadedAtKey(): string {
    return `${this.cacheKey}:downloadedAt`;
  }

  async fetch(): Promise<string[] | null> {
    const data = await AsyncStorage.getItem(this.cacheKey);
    return data ? JSON.parse(data) : null;
  }

  async remove(): Promise<void> {
    await Promise.all([
      AsyncStorage.removeItem(this.cacheKey),
      AsyncStorage.removeItem(this.downloadedAtKey),
    ]);
  }

  async saveStringified(stringifiedIds: string): Promise<void> {
    await Promise.all([
      AsyncStorage.setItem(this.cacheKey, stringifiedIds),
      AsyncStorage.setItem(this.downloadedAtKey, new Date().toISOString()),
    ]);
  }

  async save(ids: string[]): Promise<void> {
    await Promise.all([
      AsyncStorage.setItem(this.cacheKey, JSON.stringify(ids)),
      AsyncStorage.setItem(this.downloadedAtKey, new Date().toISOString()),
    ]);
  }

  async getDownloadedAt(): Promise<string | null> {
    return AsyncStorage.getItem(this.downloadedAtKey);
  }
}

export interface VariantPackTranslations {
  description?: string;
  display_name?: string;
}

export interface VariantPackEntry {
  description: string | null;
  display_name: string;
  external_item_id: string | null;
  item_uuid: string;
  order_max_quantity?: number | null;
  order_min_quantity?: number | null;
  pack_max_unit_size: number;
  pack_min_unit_size: number;
  pack_unit: string | null;
  photo_url?: string;
  translations?: VariantPackTranslations;
  // Optional: absent on offline caches downloaded before this field was added,
  // until the device re-syncs.
  unit_count?: number | null;
  upc_list: string[] | null;
  uuid: string;
}

export type MinimalVariantPackData = VariantPackEntry[];

// Used to get minimal VP info needed to display in local scan UX
export class OfflineMinimalVariantPackAdapter
  implements OfflineAdapter<MinimalVariantPackData>
{
  private static instanceMap: {
    [supplierUUID: string]: OfflineMinimalVariantPackAdapter;
  } = {};
  supplierUUID: string;

  private constructor(supplierUUID: string) {
    this.supplierUUID = supplierUUID;
  }

  public static getInstance(
    supplierUUID: string,
  ): OfflineMinimalVariantPackAdapter {
    if (!OfflineMinimalVariantPackAdapter.instanceMap[supplierUUID]) {
      OfflineMinimalVariantPackAdapter.instanceMap[supplierUUID] =
        new OfflineMinimalVariantPackAdapter(supplierUUID);
    }
    return OfflineMinimalVariantPackAdapter.instanceMap[supplierUUID];
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.MinimalVariantPack, {
      supplierUUID: this.supplierUUID,
    });
  }

  private get downloadedAtKey(): string {
    return `${this.cacheKey}:downloadedAt`;
  }

  private get queryStringKey(): string {
    return `${this.cacheKey}:queryKey`;
  }

  async fetch(): Promise<MinimalVariantPackData | null> {
    const data = await AsyncStorage.getItem(this.cacheKey);
    return data ? JSON.parse(data) : null;
  }

  async remove(): Promise<void> {
    await Promise.all([
      AsyncStorage.removeItem(this.cacheKey),
      AsyncStorage.removeItem(this.downloadedAtKey),
      AsyncStorage.removeItem(this.queryStringKey),
    ]);
  }

  async saveStringified(stringifiedEntries: string): Promise<void> {
    await Promise.all([
      AsyncStorage.setItem(this.cacheKey, stringifiedEntries),
      AsyncStorage.setItem(this.downloadedAtKey, new Date().toISOString()),
    ]);
  }

  async save(entries: MinimalVariantPackData): Promise<void> {
    await Promise.all([
      AsyncStorage.setItem(this.cacheKey, JSON.stringify(entries)),
      AsyncStorage.setItem(this.downloadedAtKey, new Date().toISOString()),
    ]);
  }

  async getDownloadedAt(): Promise<string | null> {
    return AsyncStorage.getItem(this.downloadedAtKey);
  }

  async saveQueryKey(queryString: string): Promise<void> {
    await AsyncStorage.setItem(this.queryStringKey, queryString);
  }

  async getQueryKey(): Promise<string | null> {
    return AsyncStorage.getItem(this.queryStringKey);
  }
}

export interface BackgroundSyncMetadata {
  query: string;
  syncType: OfflineSyncType;
  variables: OperationVariables;
}

export interface BackgroundSyncRegistry {
  [syncRegistryKey: string]: Partial<
    Record<OfflineSyncType, BackgroundSyncMetadata>
  >;
}

export class OfflineBackgroundSyncRegistryAdapter
  implements OfflineAdapter<BackgroundSyncRegistry>
{
  private generateSyncRegistryKey(
    restaurantUUID: string,
    supplierUUID: string,
  ) {
    return `${restaurantUUID}:${supplierUUID}`;
  }

  get cacheKey(): string {
    return getOfflineKey(OfflineData.BackgroundSync);
  }

  async fetch(): Promise<BackgroundSyncRegistry | null> {
    const backgroundSyncRegistry = await AsyncStorage.getItem(this.cacheKey);
    return backgroundSyncRegistry ? JSON.parse(backgroundSyncRegistry) : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(backgroundSyncRegistry: BackgroundSyncRegistry): Promise<void> {
    await AsyncStorage.setItem(
      this.cacheKey,
      JSON.stringify(backgroundSyncRegistry),
    );
  }

  // Incrementally updates each specific sync metadata
  async updateSyncMetadata(
    restaurantUUID: string,
    supplierUUID: string,
    metadata: BackgroundSyncMetadata,
  ): Promise<void> {
    const existingRegistry = await this.fetch();
    const syncRegistryKey = this.generateSyncRegistryKey(
      restaurantUUID,
      supplierUUID,
    );

    await this.save({
      ...(existingRegistry ?? {}),
      [syncRegistryKey]: {
        ...(existingRegistry?.[syncRegistryKey] ?? {}),
        [metadata.syncType]: metadata,
      },
    });
  }

  async removeSyncMetadata(
    restaurantUUID: string,
    supplierUUID: string,
  ): Promise<void> {
    const existingRegistry = await this.fetch();
    if (!existingRegistry) {
      return;
    }

    const syncRegistryKey = this.generateSyncRegistryKey(
      restaurantUUID,
      supplierUUID,
    );

    const newRegistry = { ...existingRegistry };
    delete newRegistry[syncRegistryKey];

    await this.save(newRegistry);
  }
}

export interface UserBackgroundSyncRegistry {
  [userUUID: string]: BackgroundSyncMetadata;
}

export class OfflineUserBackgroundSyncRegistryAdapter
  implements OfflineAdapter<UserBackgroundSyncRegistry>
{
  get cacheKey(): string {
    return getOfflineKey(OfflineData.UserBackgroundSync);
  }

  async fetch(): Promise<UserBackgroundSyncRegistry | null> {
    const userBackgroundSyncRegistry = await AsyncStorage.getItem(
      this.cacheKey,
    );
    return userBackgroundSyncRegistry
      ? JSON.parse(userBackgroundSyncRegistry)
      : null;
  }

  async remove(): Promise<void> {
    await AsyncStorage.removeItem(this.cacheKey);
  }

  async save(userRegistry: UserBackgroundSyncRegistry): Promise<void> {
    await AsyncStorage.setItem(this.cacheKey, JSON.stringify(userRegistry));
  }

  async updateUserSyncMetadata(
    userUUID: string,
    metadata: BackgroundSyncMetadata,
  ): Promise<void> {
    const existingRegistry = await this.fetch();

    await this.save({
      ...(existingRegistry ?? {}),
      [userUUID]: metadata,
    });
  }
}
